import type { JobType, MockLine, PrintState } from '../../types/print';
import type { View } from '../../types/types';
import type { PrintProps } from '../components/Print';
import { executeEthernetPrintJob } from './print/EthernetPrint';
import { executeUsbPrintJob } from './print/UsbPrint';

const getMainMessage = (state: PrintState): string => {
    const { lang, result } = state;

    if (result.type === 'test') {
        return result.isSuccess ? lang.testResult_success : lang.testResult_error;
    } else {
        return result.isSuccess ? lang.printResult_success : lang.printResult_error;
    }
};

export const executePrintJob = async (view: View<PrintState>, jobType: JobType): Promise<void> => {
    const { printType } = view.state;

    switch (printType) {
        case 'ethernet':
            await executeEthernetPrintJob(view, jobType);
            break;
        case 'usb':
            await executeUsbPrintJob(view, jobType);
            break;
        case 'mock':
            executeMockPrintJob(view, jobType);
            break;
        default:
            throw new Error('Unexpected print type: ' + printType);
    }
};

const executeMockPrintJob = async (view: View<PrintState>, jobType: JobType): Promise<void> => {
    const { state, update } = view;

    state.result = {
        type: jobType,
        responseBody: '',
        mockData: state.documents[jobType] as MockLine[],
        isSuccess: true,
    };

    state.isExecuting = false;
    update();
};

export const getPrintProps = (view: View<PrintState>): PrintProps => {
    const { state } = view;
    const { printType, lang, testMode, printerIp, isExecuting, result, hasFailed } = state;

    if (printType === 'ethernet' && !printerIp) {
        return {
            result: {
                mainMessage: lang.ipNotConfigured,
            },
        };
    }

    const isMock = printType === 'mock';
    const props: PrintProps = { isLoading: isExecuting };

    if (testMode || hasFailed) {
        props.buttons = [];

        if (!isMock) {
            props.buttons.push({
                button: {
                    children: lang.testConnection,
                    disabled: isExecuting,
                    onClick: (): Promise<void> => executePrintJob(view, 'test'),
                    class: 'btn btn-primary',
                },
            });
        }

        props.buttons.push({
            button: {
                children: testMode ? lang.printExampleTicket : lang.printAgain,
                disabled: isExecuting,
                onClick: (): Promise<void> => executePrintJob(view, 'print'),
                class: 'btn btn-primary',
            },
        });

        if (printType === 'ethernet') {
            props.buttons.push({
                link: {
                    href: `https://${printerIp}`,
                    children: lang.openWebInterface,
                    target: '_blank',
                    class: 'btn btn-primary',
                },
            });
        }
    }

    if (isMock) {
        if (state.returnUrl) {
            props.buttons = [
                ...(props.buttons ?? []),
                {
                    link: {
                        href: state.returnUrl,
                        children: 'Continue',
                        class: 'btn btn-primary',
                    },
                },
            ];
        }

        props.result = {
            mainMessage: '',
            mockData: result?.mockData,
        };
    } else if (result && (testMode || hasFailed)) {
        props.result = {
            mainMessage: getMainMessage(state),
            secondaryMessage: result.errorMessage,
            rawTitle: lang.rawResponse,
            raw: result.responseBody,
            hexStatus: result.hexStatus,
        };

        if (!result.isSuccess) {
            props.result.recommendation = lang.recommendation;
        }
    }

    return props;
};
